// src/components/DetailSidebarWatchlists.jsx
//
// Watchlist item detail / edit sidebar.
//
// Visual pattern matches the rest of the app's RF-style detail panes
// (see DetailSidebar.jsx, PaymentFraud's CardDetailDrawer): max-w-3xl panel
// with `bg-[#06101e]`, `border-sky-900/40`, `shadow-[0_0_50px_rgba(0,0,0,0.5)]`,
// slide-in transform, small icon-eyebrow + h2 header, uppercase section heads.
//
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Bookmark, Plus, Check } from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import OrgMemberPicker from "./OrgMemberPicker";

const MAX_EXTRA_RECIPIENTS = 1;
const SUBSCRIPTION_URL =
  "https://whiteintel.io/api/regular/app/v2/user/get_subscription.php";
const USER_INFO_URL =
  "https://whiteintel.io/api/regular/app/v2/user/get_user_information.php";

export default function WatchlistItemSidebar({
  open,
  itemId,
  jwtToken,
  onClose,
  size = "normal",
  detailUrl = "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
  saveUrl = "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_items_v2.php",
  backdropZ = 400,
  panelZ = 500,
  onStatusChange,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorDetail, setErrorDetail] = useState("");
  const [detail, setDetail] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  // One optional extra recipient held as an array internally so the add/
  // remove UI stays uniform; persisted as a discrete `secondary_email`
  // payload field (NOT an array).
  const [extraNotifies, setExtraNotifies] = useState([]);
  // Subscription tier — Enterprise / Threat Intel unlock the multi-notify
  // option. Fetched independently of the detail call.
  const [subscription, setSubscription] = useState(null);
  // Signed-in user's email — shown as the Notify placeholder so the fallback
  // is explicit. Fetched alongside the subscription.
  const [userEmail, setUserEmail] = useState("");
  // Does the user belong to an organization? null = unknown (still loading).
  // When false, the Notify field is locked to the user's own email and the
  // "Add another recipient" option is hidden.
  const [hasOrg, setHasOrg] = useState(null);

  const canCall = !!open && !!jwtToken && !!itemId;
  const multiNotifyAllowed =
    subscription === "tifirm" || subscription === "enterprise";

  const slackReady = !!detail?.slack_active;
  const lookalikeReady = !!detail?.lookalike_allowed;
  const jiraReady = !!detail?.jira_active;

  // Auto-uncheck toggles if their integration isn't configured.
  useEffect(() => {
    if (!detail) return;
    const needSlackOff = !slackReady && !!detail.include_slack;
    const needJiraOff = !jiraReady && !!detail.include_jira;
    if (needSlackOff || needJiraOff) {
      setDetail((d) => ({
        ...d,
        include_slack: slackReady ? !!d.include_slack : false,
        include_jira: jiraReady ? !!d.include_jira : false,
      }));
    }
  }, [slackReady, jiraReady, detail]);

  // Clear extras when the user's tier drops (same safety net as the create
  // form) so a stale secondary can never sneak into the save payload.
  useEffect(() => {
    if (!multiNotifyAllowed && extraNotifies.length > 0) setExtraNotifies([]);
  }, [multiNotifyAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch subscription tier + user email when the panel opens.
  useEffect(() => {
    if (!open || !jwtToken) return;
    (async () => {
      try {
        const res = await fetch(SUBSCRIPTION_URL, {
          method: "GET",
          headers: { Authorization: `Bearer ${jwtToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.success) setSubscription(data.subscription ?? null);
      } catch (e) {
        console.error("Fetch subscription error:", e);
      }
    })();
    (async () => {
      try {
        const res = await fetch(USER_INFO_URL, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.user?.email) setUserEmail(data.user.email);
        if (data?.user) setHasOrg(Boolean(data.user.organization_name));
      } catch (e) {
        console.error("Fetch user email error:", e);
      }
    })();
  }, [open, jwtToken]);

  // Solo users can't have additional recipients — clear any that may have
  // come back from the detail call before the org info loaded.
  useEffect(() => {
    if (hasOrg === false && extraNotifies.length > 0) setExtraNotifies([]);
    if (hasOrg === false && detail?.notify_email) {
      setDetail((d) => (d ? { ...d, notify_email: "" } : d));
    }
  }, [hasOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch item detail.
  useEffect(() => {
    if (!canCall) return;
    setLoading(true);
    setErrorDetail("");
    setSuccessMsg("");

    (async () => {
      try {
        const res = await fetch(detailUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({ id: Number(itemId), metric: "detail" }),
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const item = Array.isArray(json) ? json[0] : json;
        setDetail(item ?? null);
        // Pre-populate the extra from whatever the backend returns (when it
        // starts persisting it). Missing key collapses to an empty array,
        // which is the right "no extras" state.
        const fromDetail = [item?.secondary_email].filter(
          (e) => typeof e === "string" && e.trim().length > 0,
        );
        setExtraNotifies(fromDetail);
      } catch (e) {
        setErrorDetail(e.message || "Failed to load watchlist item");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, itemId, jwtToken, detailUrl, canCall]);

  // Save handler — sends extras as discrete fields when the tier allows.
  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = {
        id: itemId,
        metric: "update",
        include_usernames: detail.include_usernames ? 1 : 0,
        include_jira: detail.include_jira ? 1 : 0,
        include_slack: detail.include_slack ? 1 : 0,
        consumer_alerts: detail.consumer_alerts ? 1 : 0,
        corporate_alerts: detail.corporate_alerts ? 1 : 0,
        notify_email: detail.notify_email || "",
        detect_look_alike: detail.include_lookalike || 0,
        status: detail.status,
        ...(multiNotifyAllowed && extraNotifies[0]?.trim()
          ? { secondary_email: extraNotifies[0].trim() }
          : {}),
      };

      const res = await fetch(saveUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(payload),
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) throw new Error("Failed to save watchlist item");
      onStatusChange?.(itemId, detail.status);
      setSuccessMsg("Watchlist item updated successfully.");
    } catch (e) {
      console.error(e);
      setErrorDetail(e.message || "Failed to save watchlist item");
    } finally {
      setSaving(false);
    }
  };

  // Close on ESC — matches DetailSidebar.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const maxWCls = size === "wide" ? "max-w-4xl" : "max-w-3xl";

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0"
      style={{ zIndex: backdropZ }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="absolute inset-0 bg-black/60"
      />

      {/* Panel — same surface, border, shadow, slide animation as the rest
          of the app's RF-style sidebars. */}
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{ zIndex: panelZ }}
        className={`absolute right-0 top-0 h-full w-full ${maxWCls} flex flex-col border-l border-sky-900/40 bg-[#06101e] shadow-[0_0_50px_rgba(0,0,0,0.5)] transform transition-transform duration-300 translate-x-0`}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-sky-900/30 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[13px] text-neutral-400">
              <Bookmark className="h-4 w-4" />
              <span>Watchlist Item</span>
            </div>
            <h2 className="mt-1 text-[16px] font-semibold tracking-tight text-neutral-100 truncate">
              {detail?.entry || "—"}
            </h2>
            {detail?.entry_type && (
              <div className="mt-1 text-[11px] uppercase tracking-wider text-neutral-500">
                {detail.entry_type}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <IconBtn title="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </IconBtn>
          </div>
        </div>

        {/* Success banner */}
        {successMsg && (
          <div className="mx-5 mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[12.5px] text-emerald-200 flex items-start justify-between gap-3">
            <span className="min-w-0">{successMsg}</span>
            <button
              type="button"
              onClick={() => setSuccessMsg("")}
              className="text-[11px] text-neutral-400 hover:text-white shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Error banner */}
        {errorDetail && (
          <div className="mx-5 mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200 flex items-start justify-between gap-3">
            <span className="min-w-0">{errorDetail}</span>
            <button
              type="button"
              onClick={() => setErrorDetail("")}
              className="text-[11px] text-neutral-400 hover:text-white shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {loading ? (
            <Loader />
          ) : detail ? (
            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
              <Section title="Identifier">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-[12.5px]">
                  <Field label="Type"     value={detail.entry_type || "—"} />
                  <Field label="Entry"    value={detail.entry || "—"} />
                  <Field label="Added by" value={detail.added_by || "—"} />
                  <Field label="Added at" value={fmtISO(detail.added_at)} />
                </dl>
              </Section>

              <Section title="Status">
                <div className="flex gap-1.5">
                  {[
                    { v: "enabled",  l: "Enabled" },
                    { v: "disabled", l: "Disabled" },
                  ].map((o) => (
                    <RadioChip
                      key={o.v}
                      active={detail.status === o.v}
                      onClick={() => setDetail((d) => ({ ...d, status: o.v }))}
                    >
                      {o.l}
                    </RadioChip>
                  ))}
                </div>
              </Section>

              <Section title="Notify recipients">
                <input
                  type="email"
                  value={hasOrg === false ? "" : (detail.notify_email || "")}
                  onChange={(e) =>
                    setDetail((d) => ({ ...d, notify_email: e.target.value }))
                  }
                  placeholder={userEmail || "user@company.com"}
                  disabled={hasOrg === false}
                  className="w-full rounded-md text-[13px] py-2 px-3 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 placeholder:text-neutral-500 focus:border-sky-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="mt-1 text-[11px] text-neutral-500">
                  {hasOrg === false
                    ? "You don't have an organization, so alerts can only go to your own email."
                    : "All recipients must belong to your organization. If you don't have one, only your own email can be used."}
                </div>

                {/* One additional recipient — Enterprise / Threat Intel
                    only, AND only when the user belongs to an organization
                    (solo accounts have nobody else to send to). Picker is a
                    searchable dropdown of teammates — no typos possible. */}
                {hasOrg !== false && multiNotifyAllowed && extraNotifies.map((val, i) => (
                  <div key={i} className="mt-2 flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <OrgMemberPicker
                        value={val}
                        onChange={(email) =>
                          setExtraNotifies((arr) =>
                            arr.map((x, idx) => (idx === i ? email : x)),
                          )
                        }
                        jwtToken={jwtToken}
                        enabled={multiNotifyAllowed && hasOrg !== false}
                        excludeEmail={userEmail}
                        placeholder="Select a teammate…"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setExtraNotifies((arr) => arr.filter((_, idx) => idx !== i))
                      }
                      title="Remove recipient"
                      className="p-1.5 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10 transition-colors shrink-0 mt-1"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}

                {hasOrg !== false && (!multiNotifyAllowed || extraNotifies.length < MAX_EXTRA_RECIPIENTS) && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() =>
                        multiNotifyAllowed &&
                        setExtraNotifies((arr) => [...arr, ""])
                      }
                      disabled={!multiNotifyAllowed}
                      title={
                        multiNotifyAllowed
                          ? "Add another recipient"
                          : "Enterprise / Threat Intelligence required"
                      }
                      className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md border transition-colors ${
                        multiNotifyAllowed
                          ? "border-sky-900/40 bg-[#0a1224] text-neutral-300 hover:border-sky-700/50 hover:bg-sky-500/5 hover:text-neutral-100"
                          : "border-sky-900/30 bg-[#0a1224]/40 text-neutral-500 cursor-not-allowed"
                      }`}
                    >
                      <Plus className="h-3 w-3" />
                      Add another recipient
                      {multiNotifyAllowed && extraNotifies.length > 0 && (
                        <span className="text-neutral-500">
                          ({extraNotifies.length}/{MAX_EXTRA_RECIPIENTS})
                        </span>
                      )}
                      {!multiNotifyAllowed && (
                        <span className="ml-1 inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-amber-500/10 text-amber-300 ring-amber-500/30">
                          Enterprise+
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </Section>

              {/* Audience picker — only when the entry isn't a keyword. */}
              {detail.entry_type !== "keyword" && (
                <Section title="Audiences">
                  <div className="flex flex-wrap gap-2">
                    <ToggleChip
                      active={!!detail.corporate_alerts}
                      onClick={() =>
                        setDetail((d) => ({ ...d, corporate_alerts: !d.corporate_alerts }))
                      }
                    >
                      Corporate alerts
                    </ToggleChip>
                    <ToggleChip
                      active={!!detail.consumer_alerts}
                      onClick={() =>
                        setDetail((d) => ({ ...d, consumer_alerts: !d.consumer_alerts }))
                      }
                    >
                      Consumer alerts
                    </ToggleChip>
                  </div>
                </Section>
              )}

              <Section title="Delivery options">
                <div className="space-y-2">
                  {detail.entry_type === "domain" && (
                    <ToggleRow
                      label="Detect lookalike domains"
                      hint={lookalikeReady ? null : "Higher license required."}
                      checked={!!detail.include_lookalike}
                      disabled={!lookalikeReady}
                      onChange={(v) =>
                        setDetail((d) => ({ ...d, include_lookalike: v }))
                      }
                    />
                  )}
                  <ToggleRow
                    label="Push to Slack"
                    hint={slackReady ? null : "Not configured yet."}
                    checked={!!detail.include_slack}
                    disabled={!slackReady}
                    onChange={(v) =>
                      setDetail((d) => ({ ...d, include_slack: v }))
                    }
                  />
                  {detail.entry_type !== "keyword" && (
                    <ToggleRow
                      label="Push to Jira"
                      hint={jiraReady ? null : "Not configured yet."}
                      checked={!!detail.include_jira}
                      disabled={!jiraReady}
                      onChange={(v) =>
                        setDetail((d) => ({ ...d, include_jira: v }))
                      }
                    />
                  )}
                </div>
              </Section>
            </form>
          ) : (
            <div className="text-[12.5px] text-neutral-500">No data available.</div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-sky-900/30 bg-[#0a1224]/80 backdrop-blur px-5 py-3 shrink-0 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-md border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 disabled:opacity-50 disabled:hover:bg-sky-500/15"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
}

/* ---------------- atoms ---------------- */

function IconBtn({ children, title, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded text-neutral-300 ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5 hover:text-white"}`}
    >
      {children}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-3 text-[13px] font-semibold tracking-wide text-neutral-400 uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[10.5px] uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-0.5 text-neutral-100 break-all">{value}</dd>
    </div>
  );
}

function ToggleChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12.5px] transition-colors ${
        active
          ? "border-sky-500/40 bg-sky-500/15 text-sky-100"
          : "border-sky-900/40 bg-[#0a1224] text-neutral-300 hover:border-sky-700/50 hover:text-neutral-100"
      }`}
    >
      {active && <Check className="h-3 w-3" strokeWidth={3} />}
      <span>{children}</span>
    </button>
  );
}

function RadioChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center rounded-md border px-2.5 py-1.5 text-[12.5px] transition-colors ${
        active
          ? "border-sky-500/40 bg-sky-500/15 text-sky-100"
          : "border-sky-900/40 bg-[#0a1224] text-neutral-300 hover:border-sky-700/50 hover:text-neutral-100"
      }`}
    >
      {children}
    </button>
  );
}

// Single checkbox row used for the delivery-option toggles. Calm: small
// neutral row with the label on the left and a hint pill on the right when
// the option is gated/unavailable.
function ToggleRow({ label, hint, checked, disabled, onChange }) {
  return (
    <label
      className={`flex items-center gap-2 rounded-md border border-sky-900/30 bg-[#0a1224]/40 px-3 py-2 text-[12.5px] ${
        disabled ? "text-neutral-500 cursor-not-allowed" : "text-neutral-200 cursor-pointer hover:bg-sky-500/5"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-sky-500 disabled:opacity-50"
      />
      <span className="flex-1 min-w-0">{label}</span>
      {hint && (
        <span className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-amber-500/10 text-amber-300 ring-amber-500/30 shrink-0">
          {hint}
        </span>
      )}
    </label>
  );
}

function Loader() {
  return (
    <div className="space-y-3">
      <div className="h-4 w-36 bg-sky-900/30 animate-pulse rounded" />
      <div className="h-4 w-64 bg-sky-900/20 animate-pulse rounded" />
      <div className="h-4 w-48 bg-sky-900/20 animate-pulse rounded" />
    </div>
  );
}

function fmtISO(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toISOString().slice(0, 10);
}
